import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus, Search, FileText, ChevronRight, Copy, Send, Printer,
  Wrench, MoreHorizontal, X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { inr, STATUS_META } from "./lib";

const SAVED_VIEWS = [
  { key: "", label: "All" },
  { key: "draft", label: "Drafts" },
  { key: "prepared", label: "Prepared" },
  { key: "expired", label: "Expired" },
  { key: "converted", label: "Converted" },
  { key: "cancelled", label: "Cancelled" },
];

/**
 * EstimatesList — full-featured list of workshop estimates with saved views,
 * search across number / customer / vehicle, and quick actions per row.
 */
export default function EstimatesList() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [view, setView] = useState("");
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (view) params.status = view;
    if (q.trim()) params.q = q.trim();
    api.get("/estimates", { params })
      .then((r) => setRows(r.data || []))
      .catch((e) => setError(e.response?.data?.detail || "Could not load estimates"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
     
  }, [q, view]);

  const totals = useMemo(() => {
    const t = { count: rows.length, value: 0, drafts: 0, prepared: 0, converted: 0 };
    for (const r of rows) {
      t.value += Number(r.totals?.grand_total || 0);
      t[r.status] = (t[r.status] || 0) + 1;
    }
    return t;
  }, [rows]);

  return (
    <div className="space-y-5" data-testid="estimates-list-page">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <h1 className="text-[24px] md:text-[28px] font-bold text-[#1d3557] leading-tight">
            Estimates
          </h1>
          <p className="text-[13px] text-[#667085] mt-0.5">
            Quotes for your customers. Convert approved estimates into a job card in one click.
          </p>
        </div>
        <Link
          to="/estimates/new"
          data-testid="estimates-create-btn"
          className="h-11 px-4 rounded-lg bg-[#e63946] hover:bg-[#d02f3c] text-white text-[13px] font-semibold inline-flex items-center gap-2 self-start"
        >
          <Plus size={16} /> Create Estimate
        </Link>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Kpi label="Total Estimates" value={totals.count} testId="est-kpi-total" />
      </div>

      {/* Views + search */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex flex-wrap gap-1 bg-white rounded-lg border border-[#E4E7EC] p-1">
          {SAVED_VIEWS.map((v) => (
            <button
              key={v.key || "all"}
              onClick={() => setView(v.key)}
              data-testid={`est-view-${v.key || "all"}`}
              className={`h-8 px-3 rounded-md text-[11.5px] font-semibold transition-colors ${
                view === v.key
                  ? "bg-[#1d3557] text-white"
                  : "text-[#667085] hover:text-[#1d3557]"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a8dadc]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="estimates-search"
            placeholder="Search by number, customer name, phone, or vehicle reg…"
            className="w-full h-10 pl-9 pr-9 rounded-lg border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a8dadc] hover:text-[#e63946]"
              type="button"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <section className="bg-white rounded-xl border border-[#E4E7EC]">
        {loading ? (
          <div className="p-10 text-center text-[13px] text-[#667085]">Loading…</div>
        ) : error ? (
          <div className="p-10 text-center text-[13px] text-[#e63946]">{error}</div>
        ) : rows.length === 0 ? (
          <div className="p-14 text-center" data-testid="estimates-empty">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#F1FAEE] grid place-items-center text-[#457b9d] mb-3">
              <FileText size={20} />
            </div>
            <div className="text-[14px] font-semibold text-[#1d3557]">No estimates yet</div>
            <p className="text-[12px] text-[#667085] mt-1">
              Create your first estimate to share a price with your customer.
            </p>
            <Link
              to="/estimates/new"
              className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold"
            >
              <Plus size={14} /> Create Estimate
            </Link>
          </div>
        ) : (
          <>
            {/* Desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="estimates-desktop-table">
                <thead>
                  <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] border-b border-[#F1FAEE]">
                    <th className="px-5 py-2.5">Estimate</th>
                    <th className="px-3 py-2.5">Date</th>
                    <th className="px-3 py-2.5">Customer</th>
                    <th className="px-3 py-2.5">Vehicle</th>
                    <th className="px-3 py-2.5 text-right">Total</th>
                    <th className="px-3 py-2.5">Valid Until</th>
                    <th className="px-3 py-2.5">Status</th>
                    <th className="px-3 py-2.5">Job Card</th>
                    <th className="px-5 py-2.5 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <EstimateRow key={r.id} r={r} onReload={load} nav={nav} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile */}
            <ul className="md:hidden divide-y divide-[#F1FAEE]" data-testid="estimates-mobile-list">
              {rows.map((r) => (
                <li key={r.id}>
                  <Link
                    to={`/estimates/${r.id}`}
                    className="block p-4 hover:bg-[#F7F9FC]"
                    data-testid={`est-card-${r.number}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-[13px] font-semibold text-[#1d3557]">
                        {r.number}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[9.5px] font-semibold uppercase tracking-wider ${STATUS_META[r.status]?.chip}`}
                      >
                        {STATUS_META[r.status]?.label}
                      </span>
                    </div>
                    <div className="text-[13px] font-medium text-[#1d3557]">
                      {r.customer_snapshot?.name || "—"}
                    </div>
                    <div className="text-[11.5px] text-[#667085]">
                      {r.vehicle_snapshot?.reg_number || "No vehicle"} · {inr(r.totals?.grand_total)}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}

function EstimateRow({ r, onReload, nav }) {
  const status = STATUS_META[r.status] || STATUS_META.draft;
  return (
    <tr
      className="border-b border-[#F1FAEE] hover:bg-[#F7F9FC] cursor-pointer"
      onClick={() => nav(`/estimates/${r.id}`)}
      data-testid={`est-row-${r.number}`}
    >
      <td className="px-5 py-3">
        <div className="font-mono text-[12.5px] font-semibold text-[#1d3557]">{r.number}</div>
        <div className="text-[10.5px] text-[#667085]">by {r.prepared_by_name || "—"}</div>
      </td>
      <td className="px-3 py-3 text-[#667085]">{r.estimate_date}</td>
      <td className="px-3 py-3">
        <div className="font-medium text-[#1d3557]">{r.customer_snapshot?.name || "—"}</div>
        <div className="text-[11px] text-[#667085]">{r.customer_snapshot?.phone || ""}</div>
      </td>
      <td className="px-3 py-3">
        <div className="font-mono text-[12px] text-[#1d3557]">
          {r.vehicle_snapshot?.reg_number || "—"}
        </div>
        <div className="text-[11px] text-[#667085] truncate max-w-[140px]">
          {[r.vehicle_snapshot?.make, r.vehicle_snapshot?.model].filter(Boolean).join(" ")}
        </div>
      </td>
      <td className="px-3 py-3 text-right font-mono font-semibold text-[#1d3557] tabular-nums">
        {inr(r.totals?.grand_total)}
      </td>
      <td className="px-3 py-3 text-[#667085]">{r.valid_until}</td>
      <td className="px-3 py-3">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${status.chip}`}
        >
          {status.label}
        </span>
      </td>
      <td className="px-3 py-3">
        {r.converted_to_job_card_id ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              nav(`/job-cards/${r.converted_to_job_card_id}`);
            }}
            className="text-[11.5px] font-semibold text-[#12B76A] hover:underline inline-flex items-center gap-1"
          >
            <Wrench size={11} /> View JC
          </button>
        ) : (
          <span className="text-[#a8dadc] text-[11px]">—</span>
        )}
      </td>
      <td className="px-5 py-3 text-right">
        <Link
          to={`/estimates/${r.id}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 h-7 px-3 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE]"
          data-testid={`est-row-view-${r.number}`}
        >
          Open <ChevronRight size={11} />
        </Link>
      </td>
    </tr>
  );
}

function Kpi({ label, value, tone = "steel", testId }) {
  const toneMap = {
    steel: "bg-white border-[#E4E7EC]",
    blue: "bg-[#a8dadc]/20 border-[#a8dadc]",
    green: "bg-[#F1FAEE] border-[#12B76A]/20",
  };
  return (
    <div className={`rounded-xl border p-4 ${toneMap[tone]}`} data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-[#667085]">{label}</div>
      <div className="text-[26px] font-bold text-[#1d3557] font-mono tabular-nums mt-1">
        {value}
      </div>
    </div>
  );
}
